import { useState, useCallback } from 'react';
import {
  useCards,
  useSendCard,
  useEditCard,
  useRedraftCard,
  useEscalateCard,
  useClearCard,
  useCardHistory,
  useSectionCounts,
  useInboxHealth,
  useReconnectInbox,
  useOutbox,
  useCancelSend,
  useBatchSend,
  type Inbox,
  type SortMode,
  type RedraftReason,
  type EscalateTo,
} from '../hooks/useHatMail';
import EmailCard from './Card';
import CollapsedSection from './CollapsedSection';
import UndoToast, { type Toast } from './UndoToast';
import FeedbackPrompt from './FeedbackPrompt';

interface CardQueueProps {
  inbox: Inbox;
  currentUserId?: string;
  isAdmin?: boolean;
}

function parseHatMailDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const normalized = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatTime(value: string | null | undefined): string {
  const date = parseHatMailDate(value);
  if (!date) return 'Unknown';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function isStalePoll(value: string | null | undefined): boolean {
  const date = parseHatMailDate(value);
  if (!date) return true;
  return Date.now() - date.getTime() > 10 * 60 * 1000;
}

function shortError(message: string): string {
  if (message.includes('invalid_grant')) {
    return 'Google token expired or revoked.';
  }
  return message.length > 96 ? `${message.slice(0, 93)}...` : message;
}

function activityLabel(kind: string): string {
  switch (kind) {
    case 'hidden_internal':
      return 'Hidden';
    case 'card_created':
      return 'Card';
    case 'escalated':
      return 'Escalated';
    case 'classified':
      return 'Classified';
    default:
      return 'Received';
  }
}

function shortSubject(subject: string | null): string {
  if (!subject) return 'No subject';
  return subject.length > 58 ? `${subject.slice(0, 55)}...` : subject;
}

export default function CardQueue({ inbox, currentUserId, isAdmin = false }: CardQueueProps) {
  const [sort, setSort] = useState<SortMode>('smart');
  const [search, setSearch] = useState('');
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [editedIds, setEditedIds] = useState<Set<string>>(new Set());
  const activeSearch = search.trim();

  const { data, isLoading, error } = useCards(activeSearch ? undefined : inbox, sort, activeSearch);
  const history = useCardHistory(activeSearch);
  const { data: sectionCounts } = useSectionCounts();
  const { data: inboxHealth } = useInboxHealth(inbox === 'shop' ? 'shop' : 'retail');
  const { data: outboxItems } = useOutbox();

  const sendCard = useSendCard();
  const editCard = useEditCard();
  const redraftCard = useRedraftCard();
  const escalateCard = useEscalateCard();
  const clearCard = useClearCard();
  const cancelSend = useCancelSend();
  const batchSend = useBatchSend();
  const reconnectInbox = useReconnectInbox();

  // Cards eligible for batch send: high confidence, not locked
  const readyCards = (data?.cards ?? []).filter(
    (c) => c.confidence >= 0.9 && !c.locked_by,
  );
  const batchCount = readyCards.length;

  const handleBatchSend = useCallback(() => {
    if (batchCount < 2) return;
    const ids = readyCards.map((c) => c.id);
    batchSend.mutate(ids, {
      onSuccess: (result) => {
        setToasts((prev) => [
          ...prev,
          {
            id: `toast-batch-${Date.now()}`,
            name: `Sent ${result.sent} emails`,
          },
        ]);
      },
    });
  }, [readyCards, batchCount, batchSend]);

  const handleSend = useCallback(
    (id: string) => {
      const card = data?.cards.find((c) => c.id === id);
      setActiveAction(id);
      sendCard.mutate(id, {
        onSuccess: (result) => {
          setToasts((prev) => [
            ...prev,
            {
              id: `toast-${Date.now()}`,
              name: card?.extracted_customer_name || card?.from_name || card?.from_email || 'Unknown',
              outboxId: result.outbox_id,
              scheduledSendAt: result.scheduled_send_at,
            },
          ]);
          setActiveAction(null);
        },
        onError: () => setActiveAction(null),
      });
    },
    [sendCard, data?.cards],
  );

  const handleEdit = useCallback(
    (id: string, body: string, attachmentIds?: string[]) => {
      setActiveAction(id);
      editCard.mutate(
        { id, edited_body: body, attachment_ids: attachmentIds },
        {
          onSuccess: () => {
            setEditedIds((prev) => new Set(prev).add(id));
          },
          onSettled: () => setActiveAction(null),
        },
      );
    },
    [editCard],
  );

  const handleRedraft = useCallback(
    (id: string, reason: RedraftReason, note?: string) => {
      setActiveAction(id);
      redraftCard.mutate(
        { id, reason, note },
        {
          onSettled: () => setActiveAction(null),
        },
      );
    },
    [redraftCard],
  );

  const handleEscalate = useCallback(
    (id: string, escalate_to: EscalateTo, note?: string) => {
      setActiveAction(id);
      escalateCard.mutate(
        { id, escalate_to, note },
        {
          onSettled: () => setActiveAction(null),
        },
      );
    },
    [escalateCard],
  );

  const handleClear = useCallback(
    (id: string, note?: string) => {
      setActiveAction(id);
      clearCard.mutate(
        { id, note },
        {
          onSettled: () => setActiveAction(null),
        },
      );
    },
    [clearCard],
  );

  const handleUndo = useCallback(
    (outboxId: string) => {
      cancelSend.mutate(outboxId);
    },
    [cancelSend],
  );

  const handleDismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <div className="mb-3 h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-ghw-gold" />
        <p className="text-sm">Loading emails...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="rounded-xl bg-red-50 p-6 text-center">
        <p className="text-sm font-medium text-red-700">Could not load emails</p>
        <p className="mt-1 text-xs text-red-500">{(error as Error).message}</p>
      </div>
    );
  }

  const cards = data?.cards ?? [];
  const counts = sectionCounts;
  const inboxLabel = inbox === 'shop' ? 'shop@' : 'retail@';
  const lastPollLabel = formatTime(inboxHealth?.last_poll_time);
  const latestEmailLabel = formatTime(inboxHealth?.latest_email_created);
  const latestCardLabel = formatTime(inboxHealth?.latest_card_created);
  const latestHidden = inboxHealth?.latest_hidden_internal;
  const hasTokenError = Boolean(inboxHealth?.last_token_error);
  const pollStale = inboxHealth ? isStalePoll(inboxHealth.last_poll_time) : false;
  const needsReconnect = inboxHealth ? hasTokenError || pollStale : false;
  const hasRecentHiddenOnly =
    !!inboxHealth &&
    inboxHealth.received_24h > 0 &&
    inboxHealth.pending_cards === 0 &&
    inboxHealth.hidden_internal_24h > 0;
  const connectionStatus = inboxHealth
    ? hasTokenError
      ? `${inboxLabel} is disconnected. ${shortError(inboxHealth.last_token_error?.message ?? '')}`
      : pollStale
        ? `${inboxLabel} has not checked in. Last checked ${lastPollLabel}.`
        : `${inboxLabel} is connected. Last checked ${lastPollLabel}.`
    : `${inboxLabel} status is loading.`;
  const activityStatus = inboxHealth
    ? needsReconnect
      ? 'Reconnect required before new messages can appear here.'
      : hasRecentHiddenOnly && latestHidden
      ? `Last message was hidden as ${latestHidden.category?.toLowerCase() ?? 'internal'} mail: ${shortSubject(latestHidden.subject)}.`
      : inboxHealth.pending_cards > 0
        ? `${inboxHealth.pending_cards} customer response card${inboxHealth.pending_cards === 1 ? '' : 's'} pending.`
        : inboxHealth.received_24h > 0
          ? `${inboxHealth.received_24h} received in the last 24 hours, none currently need a response card.`
          : 'No messages received in the last 24 hours.'
    : '';

  return (
    <div>
      {inboxHealth && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-3">
          <div
            className={`mb-3 rounded-md px-3 py-2 ${
              needsReconnect ? 'bg-red-50' : 'bg-emerald-50'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={`text-sm font-semibold ${needsReconnect ? 'text-red-900' : 'text-emerald-900'}`}>
                  {connectionStatus}
                </p>
                <p className={`mt-0.5 text-xs ${needsReconnect ? 'text-red-800' : 'text-emerald-800'}`}>
                  {activityStatus}
                </p>
              </div>
              {needsReconnect && isAdmin && (inbox === 'retail' || inbox === 'shop') && (
                <button
                  type="button"
                  onClick={() => reconnectInbox.mutate(inbox)}
                  disabled={reconnectInbox.isPending}
                  className="shrink-0 rounded-md bg-red-700 px-3 py-2 text-xs font-semibold text-white transition hover:bg-red-800 disabled:cursor-wait disabled:opacity-70"
                  style={{ minHeight: '36px' }}
                >
                  {reconnectInbox.isPending ? 'Opening...' : 'Reconnect'}
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-5 gap-2 text-center">
            <div>
              <p className="text-[11px] font-medium uppercase text-gray-400">Received</p>
              <p className="text-sm font-semibold text-gray-800">{inboxHealth.received_24h}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase text-gray-400">Cards</p>
              <p className="text-sm font-semibold text-gray-800">{inboxHealth.pending_cards}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase text-gray-400">Hidden</p>
              <p className="text-sm font-semibold text-gray-800">{inboxHealth.hidden_internal_24h}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase text-gray-400">Email</p>
              <p className="text-sm font-semibold text-gray-800">{latestEmailLabel}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase text-gray-400">Card</p>
              <p className="text-sm font-semibold text-gray-800">{latestCardLabel}</p>
            </div>
          </div>
          {inboxHealth.recent_activity.length > 0 && (
            <div className="mt-3 divide-y divide-gray-100 rounded-md border border-gray-100">
              {inboxHealth.recent_activity.slice(0, 3).map((activity) => (
                <div key={`${activity.email_id}-${activity.kind}`} className="flex items-start gap-2 px-3 py-2">
                  <div className="min-w-[64px] text-[11px] font-semibold uppercase text-gray-400">
                    {activityLabel(activity.kind)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-gray-700">{shortSubject(activity.subject)}</p>
                    <p className="truncate text-[11px] text-gray-500">{activity.reason}</p>
                  </div>
                  <div className="text-[11px] text-gray-400">{formatTime(activity.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sort toggle */}
      <div className="mb-4 space-y-2">
        <label className="block">
          <span className="sr-only">Search messages and older cards</span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            type="search"
            inputMode="search"
            placeholder="Search messages, orders, and older cards"
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm outline-none transition focus:border-ghw-gold focus:ring-2 focus:ring-ghw-gold/20"
          />
        </label>
        <div className="flex items-center gap-1 rounded-xl bg-gray-100 p-1">
          {(['smart', 'newest', 'oldest'] as SortMode[]).map((s) => (
            <button
              key={s}
              onClick={() => setSort(s)}
              className={`flex-1 rounded-lg px-3 py-2 text-xs font-medium capitalize transition-colors ${
                sort === s
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={{ minHeight: '36px' }}
            >
              {s === 'smart' ? 'Smart' : s === 'newest' ? 'Newest' : 'Oldest'}
            </button>
          ))}
        </div>
      </div>

      {/* Batch Send */}
      {batchCount > 1 && (
        <button
          onClick={handleBatchSend}
          disabled={batchSend.isPending}
          className="btn-batch-send mb-4"
        >
          {batchSend.isPending
            ? 'Sending...'
            : `Send All Ready (${batchCount})`}
        </button>
      )}

      {/* Cards */}
      {cards.length === 0 ? (
        activeSearch && (history.isLoading || history.isError || (history.data?.cards.length ?? 0) > 0) ? null : (
        <div className="py-16 text-center">
          <p className="mb-2 text-3xl">&#10003;</p>
          <p className="text-lg font-semibold text-gray-800">
            {activeSearch ? 'No matches' : 'All caught up!'}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {activeSearch
              ? 'No pending or older messages match that search'
              : inboxHealth
              ? `${connectionStatus} ${activityStatus}`
              : counts
                ? `${counts.sent_today + counts.auto_sent} handled today`
              : 'Nothing needs your attention right now'}
          </p>
        </div>
        )
      ) : (
        <div className="space-y-3">
          {activeSearch && (
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Pending</h2>
          )}
          {cards.map((card, index) => (
            <div key={card.id}>
              <EmailCard
                card={card}
                currentUserId={currentUserId}
                onSend={handleSend}
                onEdit={handleEdit}
                onRedraft={handleRedraft}
                onEscalate={handleEscalate}
                onClear={handleClear}
                sending={activeAction === card.id && sendCard.isPending}
                editing={activeAction === card.id && editCard.isPending}
                redrafting={activeAction === card.id && redraftCard.isPending}
                escalating={activeAction === card.id && escalateCard.isPending}
                clearing={activeAction === card.id && clearCard.isPending}
                isFirst={index === 0}
                showInboxTag={!!activeSearch}
              />
              {editedIds.has(card.id) && (
                <div className="mt-2">
                  <FeedbackPrompt
                    responseId={card.id}
                    onDismiss={() => setEditedIds((prev) => {
                      const next = new Set(prev);
                      next.delete(card.id);
                      return next;
                    })}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {activeSearch && (
        <section className="mt-6">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Older messages</h2>
          {history.isLoading && (
            <p className="text-sm text-gray-500">Searching older messages...</p>
          )}
          {history.isError && (
            <p className="text-sm text-red-600">Could not search older messages.</p>
          )}
          {history.data && history.data.cards.length === 0 && !history.isLoading && cards.length > 0 && (
            <p className="text-sm text-gray-500">No older messages match that search.</p>
          )}
          {history.data && history.data.truncated && (
            <p className="mb-2 text-xs text-gray-500">Showing the 40 newest matches. Narrow the search to see fewer.</p>
          )}
          <div className="space-y-3">
            {history.data?.cards.map((card) => (
              <EmailCard
                key={card.id}
                card={card}
                currentUserId={currentUserId}
                onSend={handleSend}
                onEdit={handleEdit}
                onRedraft={handleRedraft}
                onEscalate={handleEscalate}
                onClear={handleClear}
                sending={false}
                editing={false}
                redrafting={false}
                escalating={false}
                clearing={false}
                showInboxTag
                readOnly
              />
            ))}
          </div>
        </section>
      )}

      {/* Collapsed sections */}
      {counts && (
        <div className="mt-6 rounded-xl border border-gray-200 bg-white overflow-hidden">
          <CollapsedSection icon="&#10003;" label="Sent Today" count={counts.sent_today}>
            <p className="text-sm text-gray-500">Emails you reviewed and sent today.</p>
          </CollapsedSection>

          <CollapsedSection icon="&#129302;" label="Auto-Sent" count={counts.auto_sent}>
            <p className="text-sm text-gray-500">High-confidence emails sent automatically.</p>
          </CollapsedSection>

          <CollapsedSection icon="&#129309;" label="Auto-Resolved" count={counts.auto_resolved}>
            <p className="text-sm text-gray-500">Emails resolved without needing a reply.</p>
          </CollapsedSection>

          <CollapsedSection icon="&#128222;" label="Cleared today" count={counts.cleared_today}>
            <p className="text-sm text-gray-500">Cards staff cleared with no email reply, such as a customer who called.</p>
          </CollapsedSection>

          <CollapsedSection icon="&#128465;" label="Spam / Filtered" count={counts.spam_filtered}>
            <p className="text-sm text-gray-500">Filtered out as spam or irrelevant.</p>
          </CollapsedSection>

          <CollapsedSection icon="&#128228;" label="Outbox" count={outboxItems?.length ?? counts.outbox}>
            {outboxItems && outboxItems.length > 0 ? (
              <div className="space-y-2">
                {outboxItems.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm ${item.status === 'failed' ? 'bg-red-50' : 'bg-gray-50'}`}
                  >
                    <span className={item.status === 'failed' ? 'text-red-800' : 'text-gray-700'}>
                      To: {item.to_email} &middot; {item.status === 'failed' ? 'Failed' : `${item.seconds_remaining}s`}
                      {item.status === 'failed' && item.last_error && <span className="block text-xs text-red-600">{item.last_error}</span>}
                    </span>
                    <button
                      onClick={() => cancelSend.mutate(item.id)}
                      className="text-xs font-medium text-red-500 hover:text-red-700"
                    >
                      {item.status === 'failed' ? 'Restore' : 'Cancel'}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No pending sends.</p>
            )}
          </CollapsedSection>
        </div>
      )}

      {/* Undo toasts */}
      <UndoToast toasts={toasts} onUndo={handleUndo} onDismiss={handleDismissToast} />
    </div>
  );
}
